import React, { useEffect, useState } from 'react';
import { Alert, Box, Card, CardContent, Chip, Stack, Tooltip, Typography, CircularProgress } from '@mui/material';
import { CheckCircleOutline, CancelOutlined, ErrorOutline, ChatBubbleOutline } from '@mui/icons-material';
import Scrollbars from 'react-custom-scrollbars';
import Services from 'utils/serviciosVentas';

const ESTATUS_NO_ENTREGADO = 3;

const CATALOGO_ESTATUS = {
    1: { label: 'Aprobado', color: 'success', icon: <CheckCircleOutline fontSize="small" /> },
    2: { label: 'No aprobado', color: 'warning', icon: <CancelOutlined fontSize="small" /> },
    3: { label: 'No entregado', color: 'error', icon: <ErrorOutline fontSize="small" /> }
};

// Si llega un valor fuera del catalogo, se asume No aprobado
const obtenerEstatus = (valor) => CATALOGO_ESTATUS[Number(valor)] || CATALOGO_ESTATUS[2];

const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    const partes = String(fecha).split(' ')[0].split('-');
    return partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : fecha;
};

const obtenerTextoRetroalimentacion = (material) => {
    if (material.retroalimentacion) return material.retroalimentacion;

    if (Number(material.estatusCalificacion) === ESTATUS_NO_ENTREGADO) {
        return `No se cargó evidencia. El plazo venció el ${formatearFecha(material.fechaLimite)}.`;
    }

    return 'Sin comentarios por el momento.';
};

const MisCalificaciones = ({ usuario, curso }) => {
    const services = Services();
    const [sesiones, setSesiones] = useState([]);
    const [cargando, setCargando] = useState(true);

    useEffect(() => {
        if (!curso?.idCurso || !usuario?.idColaborador) {
            setCargando(false);
            return;
        }

        services.getMisCalificacionesEvidencia(
            (res) => {
                setSesiones(res?.sesiones || []);
                setCargando(false);
            },
            { idCurso: curso.idCurso, idColaborador: usuario.idColaborador }
        );
    }, [curso?.idCurso]);

    if (cargando) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (sesiones.length === 0) {
        return <Alert severity="info">Este curso no tiene materiales que requieran cargar evidencia.</Alert>;
    }

    return (
        <Scrollbars style={{ height: '70vh' }}>
            <Stack spacing={1.5} sx={{ p: 1 }}>
                {sesiones.map((sesion) => (
                    <Card key={`sesion-evid-${sesion.idSesion}`} variant="outlined">
                        <CardContent sx={{ pb: '16px !important' }}>
                            <Typography variant="h6" sx={{ mb: 1.5 }}>
                                {sesion.titulo}
                            </Typography>

                            <Stack spacing={1}>
                                {sesion.materiales.map((material) => {
                                    const estatus = obtenerEstatus(material.estatusCalificacion);
                                    const tieneComentario = Boolean(material.retroalimentacion);

                                    return (
                                        <Tooltip
                                            key={`mat-evid-${material.idDetalleActividad}`}
                                            arrow
                                            placement="top"
                                            title={
                                                <Box sx={{ p: 0.5 }}>
                                                    <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                                                        Retroalimentación del instructor
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        {obtenerTextoRetroalimentacion(material)}
                                                    </Typography>
                                                </Box>
                                            }
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        backgroundColor: '#ffffff',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        boxShadow: 2,
                                                        maxWidth: 280
                                                    }
                                                },
                                                arrow: { sx: { color: '#ffffff' } }
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 1,
                                                    p: 1,
                                                    borderRadius: 1,
                                                    backgroundColor: 'background.default',
                                                    cursor: 'default'
                                                }}
                                            >
                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Typography variant="body2" noWrap>
                                                        {material.titulo}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {material.tituloActividad}
                                                    </Typography>
                                                </Box>

                                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                                                    <Chip size="small" color={estatus.color} icon={estatus.icon} label={estatus.label} />
                                                    {tieneComentario && (
                                                        <ChatBubbleOutline sx={{ color: 'primary.main', fontSize: '1rem' }} />
                                                    )}
                                                </Stack>
                                            </Box>
                                        </Tooltip>
                                    );
                                })}
                            </Stack>
                        </CardContent>
                    </Card>
                ))}
            </Stack>
        </Scrollbars>
    );
};

export default MisCalificaciones;
